/**
 * @file PWA : enregistrement du service worker (production, navigateur uniquement — pas dans l'application Capacitor native), detection des mises a jour et
 * invite d'installation (`beforeinstallprompt`). Aucun effet en developpement (HMR) ni dans la galerie.
 */

interface InstallEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Listener = () => void;

let deferred: InstallEvent | null = null;
let waiting: ServiceWorker | null = null;
const listeners = new Set<Listener>();
const notify = (): void => listeners.forEach((l) => l());

/** Vrai dans l'application native (Capacitor injecte `window.Capacitor`). */
export const isNativeApp = (): boolean => {
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return cap?.isNativePlatform?.() === true;
};

/** Deja installee (mode standalone) ou lancee depuis l'ecran d'accueil. */
export const isInstalled = (): boolean => window.matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;

export const canInstall = (): boolean => deferred !== null && !isInstalled();
export const updateReady = (): boolean => waiting !== null;

/** Affiche l'invite d'installation du navigateur (si disponible). */
export const promptInstall = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
  if (!deferred) return 'unavailable';
  const ev = deferred;
  deferred = null;
  await ev.prompt();
  const { outcome } = await ev.userChoice;
  notify();
  return outcome;
};

/** Active la nouvelle version du service worker (la page se recharge alors toute seule). */
export const applyUpdate = (): void => {
  waiting?.postMessage('SKIP_WAITING');
};

/** Abonnement aux changements (installation possible, mise a jour prete). Retourne la desinscription. */
export const onPwaChange = (fn: Listener): (() => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const registerPwa = (): void => {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
  if (!import.meta.env.PROD || isNativeApp() || !('serviceWorker' in navigator)) return;
  // controleur deja actif au chargement : seule une MISE A JOUR recharge la page (pas la toute premiere installation)
  const hadController = navigator.serviceWorker.controller !== null;
  const start = (): void => {
    navigator.serviceWorker
      .register(`${import.meta.env.BASE_URL}sw.js`)
      .then((reg) => {
        const track = (w: ServiceWorker | null): void => {
          if (!w) return;
          w.addEventListener('statechange', () => {
            if (w.state === 'installed' && navigator.serviceWorker.controller) {
              waiting = w;
              notify();
            }
          });
        };
        track(reg.installing);
        reg.addEventListener('updatefound', () => track(reg.installing));
        // verification periodique (application laissee ouverte)
        setInterval(() => void reg.update(), 60 * 60 * 1000);
      })
      .catch((err: unknown) => console.warn('service worker non enregistre', err));
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading || !hadController) return;
      reloading = true;
      window.location.reload();
    });
  };
  // `load` peut deja etre passe (le jeu demarre en asynchrone)
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start);
};
